/**
 * CertShieldHeader — shared umbrella chrome at the top of each tool's sidebar.
 *
 * Renders: CertShield (umbrella wordmark) › {currentTool} on the left,
 * plus an apps/switch menu on the right that lets analysts hop to sibling
 * tools without re-authenticating (Keycloak SSO session is shared across
 * every tool under jdosborne.com).
 *
 * Identical file ships in both the domain-monitor frontend and the
 * emaildetection frontend. Update SIBLING_TOOLS in both when new CertShield
 * tools come online.
 */
import React, { useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { OIDC_ENABLED } from '../auth/oidcConfig';
import { Apps as AppsIcon } from '@mui/icons-material';
import LogoutButton from './LogoutButton';

const SIBLING_TOOLS = [
  { name: 'DomainProtection', tagline: 'Brand Protection', url: '/domainprotection/', accent: '#c85448' },
  { name: 'PhishDesk',        tagline: 'Phishing Triage',  url: '/phishdesk/', accent: '#a78bfa' },
];

export default function CertShieldHeader({ currentTool }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  return (
    <Box sx={{
      px: 1.75, py: 0.9,
      borderBottom: '1px solid rgba(255,255,255,0.07)',
      bgcolor: 'rgba(0,0,0,0.22)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 1,
      position: 'relative',
      // Tool accent: a quiet gradient hairline under the umbrella bar
      '&::after': {
        content: '""', position: 'absolute', left: 0, right: 0, bottom: -1, height: 2,
        background: 'linear-gradient(90deg, rgba(200,84,72,0.55) 0%, rgba(167,139,250,0.35) 45%, rgba(167,139,250,0) 85%)',
        pointerEvents: 'none',
      },
    }}>
      <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
        <Typography component="span" sx={{
          fontFamily: '"Inter", system-ui, sans-serif',
          fontWeight: 700, fontSize: '0.82rem',
          color: '#e8e4f8', letterSpacing: -0.2, whiteSpace: 'nowrap',
        }}>
          {/* TEMP (branding hidden for demo build): was "CertShield". See BRANDING_NEUTRALIZATION.md */}
          Security Tools
        </Typography>
        <Typography component="span" sx={{ color: 'rgba(232,228,248,0.28)', fontSize: '0.9rem' }}>›</Typography>
        <Typography component="span" sx={{
          fontSize: '0.78rem', fontWeight: 500,
          color: 'rgba(232,228,248,0.55)', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {currentTool}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Tooltip title="Switch tool">
          <IconButton
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-label="Switch tool"
            sx={{ color: 'rgba(232,228,248,0.4)', '&:hover': { color: '#e8e4f8' } }}
          >
            <AppsIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {OIDC_ENABLED && <LogoutButton />}
      </Box>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top',    horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 240 } }}
      >
        {SIBLING_TOOLS.map((t) => {
          const isCurrent = t.name === currentTool;
          return (
            <MenuItem
              key={t.name}
              disabled={isCurrent}
              onClick={() => {
                setAnchor(null);
                if (!isCurrent) {
                  window.location.href = t.url;
                }
              }}
              sx={{ alignItems: 'flex-start', py: 1.1 }}
            >
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: t.accent, mt: 0.9, mr: 1.5, flexShrink: 0,
              }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#e8e4f8' }}>
                  {t.name}{isCurrent ? ' (current)' : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.45)' }}>
                  {t.tagline}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
